import { fromMajor, toMajorString } from '@nucleus-commerce/core';

import type { FilterType } from './types';

/*
 * A `number-range` filter with a `currency` is a money range: the param
 * contract carries integer minor units (`total_gte=129900`), people read and
 * type major units ("1299.00"). These convert at that edge, through the same
 * `fromMajor` / `toMajorString` every money input uses.
 */

interface RangeField {
  type: FilterType;
  currency?: string;
}

/** The filter's currency when it is a money range. */
export function moneyCurrency(def: RangeField): string | undefined {
  return def.type === 'number-range' ? def.currency : undefined;
}

/** A stored bound as shown: `"129900"` -> `"1299.00"`. Other filters, and malformed bounds, pass through. */
export function toDisplayBound(def: RangeField, bound: string): string {
  const currency = moneyCurrency(def);
  if (!currency || !/^-?\d+$/.test(bound)) return bound;
  return toMajorString({ amount: Number(bound), currency });
}

/**
 * A typed bound as stored: `"1299.5"` -> `"129950"`. Undefined when it is not
 * an amount. Other filters pass through.
 */
export function toStoredBound(def: RangeField, input: string): string | undefined {
  const currency = moneyCurrency(def);
  if (!currency) return input;
  try {
    return String(fromMajor(input, currency).amount);
  } catch {
    return undefined;
  }
}

/** A stored bound for a chip: `"129900"` -> `"$1,299.00"`. */
export function formatMoneyBound(currency: string, bound: string, locale?: string): string {
  if (!/^-?\d+$/.test(bound)) return bound;
  const major = toMajorString({ amount: Number(bound), currency });
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(
    major as Intl.StringNumericLiteral,
  );
}
